import { Injectable, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
    providedIn: 'root'
})
export class GenericModalService {

    public messageEvent: (settings: ModalSettingsBase) => Promise<any>;

    constructor(private modal: NgbModal) { }

    message(settings: ModalSettingsMessage): Promise<any> {
        return this.messageEvent(settings);
    }

    messageDirect(title?: string, body?: string): Promise<any> {
        return this.messageEvent(new ModalSettingsMessage(title, body));
    }

    confirm(settings: ModalSettingsConfirm): Promise<any> {
        return this.messageEvent(settings);
    }

    confirmDirect(title?: string, body?: string, okButtonText?: string, cancelButtonText?: string): Promise<any> {
        return this.messageEvent(new ModalSettingsConfirm(title, body, okButtonText, cancelButtonText));
    }

    confirmMultiButton(settings: ModalSettingsConfirmMultiButton): Promise<any> {
        return this.messageEvent(settings);
    }

    confirmMultiButtonDirect(title?: string, body?: string, buttons: ModalMultiButton[] = []): Promise<any> {
        const settings = new ModalSettingsConfirmMultiButton(title, body);
        settings.buttons = buttons;
        return this.messageEvent(settings);
    }

    selectMultiChoice(settings: ModalSettingsSelectMultiChoice): Promise<ModalSelectElement | ModalSelectElement[]> {
        return this.messageEvent(settings);
    }

    selectMultiChoiceDirect(
        title?: string, body?: string, elements: ModalSelectElement[] = [],
        selectedElement?: ModalSelectElement, type: SelectMultiChoiceTypes = SelectMultiChoiceTypes.DropDown,
        okButtonText?: string, cancelButtonText?: string): Promise<ModalSelectElement | ModalSelectElement[]> {
        const settings = new ModalSettingsSelectMultiChoice(title, body, type, okButtonText, cancelButtonText);
        settings.selectedElement = selectedElement;
        settings.elements = elements;
        settings.multiChoiceType = type;
        return this.messageEvent(settings);
    }

    input(settings: ModalSettingsInput): Promise<string> {
        return this.messageEvent(settings);
    }

    inputDirect(title?: string, body?: string, okButtonText?: string, cancelButtonText?: string, multiline: boolean = false, rows?: number, cols?: number): Promise<string> {
        const settings = new ModalSettingsInput(title, body, okButtonText, cancelButtonText);
        settings.multiline = multiline;
        settings.multilineRows = rows || settings.multilineRows;
        settings.multilineColumns = cols || settings.multilineColumns;
        return this.messageEvent(settings);
    }

    changePassword(settings: ModalSettingsChangePassword): Promise<any> {
        return this.messageEvent(settings);
    }

    changePasswordDirect(title?: string, body?: string, okButtonText?: string, cancelButtonText?: string): Promise<any> {
        const settings = new ModalSettingsChangePassword(title, body, okButtonText, cancelButtonText);
        return this.messageEvent(settings);
    }

    openTemplate(template: TemplateRef<any>, settings: ModalSettingsTemplate): Promise<any> {
        return this.modal.open(template, settings.generateNgbOptions()).result;
    }

    openComponentType<TResult>(componentClass: any, componentValues?: any, settings?: ModalSettingsTemplate): Promise<TResult> {
        return this.openComponent(componentClass, componentValues, settings);
    }

    openComponentWithInitialize<TReturn, TComponent>(componentClass: any, settings?: ModalSettingsTemplate, initialize?: (component: TComponent) => void): Promise<TReturn> {
        return this.openComponent(componentClass, undefined, settings, initialize);
    }

    openComponentWithSignalInputs<TReturn, TComponent extends object>(componentClass: any, settings: ModalSettingsTemplate, inputValues: Partial<Record<Extract<keyof TComponent, string>, unknown>>): Promise<TReturn> {
        settings ??= new ModalSettingsTemplate('xl', 'static');
        const ref: NgbModalRef = this.modal.open(componentClass, settings.generateNgbOptions());

        const directComponentRef = (ref as unknown as { componentRef?: { setInput: (name: string, value: unknown) => void } }).componentRef;
        const internalContentComponentRef = (ref as unknown as { _contentRef?: { componentRef?: { setInput: (name: string, value: unknown) => void } } })._contentRef?.componentRef;

        const componentRef = directComponentRef?.setInput
            ? directComponentRef
            : internalContentComponentRef?.setInput
                ? internalContentComponentRef
                : undefined;
        if (!componentRef) {
            throw new Error('openComponentWithSignalInputs could not resolve a componentRef with setInput. Signal input binding is unavailable for this modal ref.');
        }

        for (const [key, value] of Object.entries(inputValues)) {
            componentRef.setInput(key, value);
        }
        return ref.result;
    }

    openComponent(componentClass: any, componentValues?: any, settings?: ModalSettingsTemplate, initialize?: (component: any) => void): Promise<any> {
        settings ??= new ModalSettingsTemplate();
        const ref: NgbModalRef = this.modal.open(componentClass, settings.generateNgbOptions());
        if (initialize) {
            initialize(ref.componentInstance);
        }
        Object.assign(ref.componentInstance, componentValues);
        return ref.result;
    }

    fileSelector(settings: ModalSettingsFileSelector): Promise<any> {
        return this.messageEvent(settings);
    }

    fileSelectorDirect(title?: string, body?: string, allowMultiple: boolean = false, okButtonText?: string,
        cancelButtonText?: string, fileFilter: string = '*.*', maxFiles?: number, maxFileSize?: number): Promise<File[]> {
        const settings = new ModalSettingsFileSelector(title, body, allowMultiple, okButtonText, cancelButtonText, fileFilter, maxFiles, maxFileSize);
        settings.backdrop = 'static';
        return this.messageEvent(settings);
    }

    jsonViewer(settings: ModalSettingsJsonViewer): Promise<any> {
        return this.messageEvent(settings);
    }

    jsonViewerDirect(title?: string, body?: string, objectToShow?: any): Promise<File[]> {
        const settings = new ModalSettingsJsonViewer(title, body, objectToShow);
        return this.messageEvent(settings);
    }

    /**
     * Not working correctly at the moment...!
     */
    datetimePicker(settings: ModalSettingsDateTime): Promise<Date | string> {
        return this.messageEvent(settings);
    }

    /**
     * Not working correctly at the moment...!
     */
    datetimePickerDirect(title?: string, body?: string, type: string = 'date', okButtonText?: string, cancelButtonText?: string): Promise<Date | string> {
        const settings = new ModalSettingsDateTime(title, body, okButtonText, cancelButtonText);
        settings.type = type;
        return this.messageEvent(settings);
    }
}

/**
 * An internal service allowing to access, from the modal component, the options and the modal reference.
 * It also allows registering the TemplateRef containing the confirm modal component.
 *
 * It must be declared in the providers of the NgModule, but is not supposed to be used in application code
 */
@Injectable({
    providedIn: 'root'
})
export class ModalState {
    /**
     * The last options passed ConfirmService.confirm()
     */
    options: ModalSettingsBase;

    /**
     * The last opened confirmation modal
     */
    modal: NgbModalRef;
}

export enum ModalTypes {
    Message,
    ChangePassword,
    Confirm,
    ConfirmMultiButton,
    SelectMultiChoice,
    Input,
    Template,
    FileSelector,
    DateTime,
    JsonViewer
}

/**
 * An interface used for all settings used in the generic dialogs.
 */
export abstract class ModalSettingsBase {
    /**
     * Type: boolean | "static"
     * Whether a backdrop element should be created for a given modal (true by default). Alternatively, specify 'static' for a backdrop which doesn't close the modal on click.
     */
    backdrop: any;
    /**
     * Type: boolean
     * Whether to close the modal when escape key is pressed (true by default).
     */
    keyboard: boolean;
    /**
     * Type: 'sm' | 'lg' | 'xl'
     * Size of a new modal window.
     */
    size: string;

    /**
     * Type: string
     * An element to which to attach newly opened modal windows.
     */
    container: string;

    /**
     * Type: string
     * Custom class to append to the modal window
     */
    windowClass: string;

    /**
     * A custom class to append to the modal backdrop.
     */
    backdropClass: string;

    abstract modalType: ModalTypes;

    /**
     * Generates an object which can be used in the open method of the ngbModal
     */
    generateNgbOptions(): NgbModalOptions {
        return {
            backdrop: this.backdrop,
            keyboard: this.keyboard,
            size: this.size,
            windowClass: this.windowClass,
            backdropClass: this.backdropClass,
            container: this.container
        };
    }
}

export class ModalSettingsTemplate extends ModalSettingsBase {
    constructor(size?: string, backdrop?: string) {
        super();
        this.size = size;
        this.backdrop = backdrop;
    }

    modalType: ModalTypes = ModalTypes.Message;
}

export class ModalSettingsMessage extends ModalSettingsBase {

    constructor(title?: string, body?: string) {
        super();
        this.title = title;
        this.body = body;
        this.backdrop = 'static';
    }

    title: string;
    body: string;
    modalType: ModalTypes = ModalTypes.Message;
}

export class ModalSettingsConfirm extends ModalSettingsMessage {

    constructor(title?: string, body?: string, okButtonText?: string, cancelButtonText?: string) {
        super(title, body);
        this.okButtonText = okButtonText;
        this.cancelButtonText = cancelButtonText;
    }

    okButtonText: string;
    cancelButtonText: string;

    override modalType: ModalTypes = ModalTypes.Confirm;
}

export class ModalSettingsJsonViewer extends ModalSettingsMessage {

    constructor(title?: string, body?: string, public objectToShow?: any) {
        super(title, body);
    }

    override modalType: ModalTypes = ModalTypes.JsonViewer;
}

export class ModalSettingsInput extends ModalSettingsConfirm {

    constructor(title?: string, body?: string, okButtonText?: string, cancelButtonText?: string) {
        super(title, body, okButtonText, cancelButtonText);
    }

    multiline = false;
    multilineRows = 10;
    multilineColumns = 60;

    type = 'text';
    initialValue: string;

    override modalType: ModalTypes = ModalTypes.Input;
}

export class ModalSettingsChangePassword extends ModalSettingsConfirm {

    constructor(title?: string, body?: string, okButtonText?: string, cancelButtonText?: string) {
        super(title, body, okButtonText, cancelButtonText);
    }

    override modalType: ModalTypes = ModalTypes.ChangePassword;
}

export class ModalSettingsConfirmMultiButton extends ModalSettingsMessage {

    constructor(title?: string, body?: string) {
        super(title, body);
    }

    buttons: ModalMultiButton[] = [];
    override modalType: ModalTypes = ModalTypes.ConfirmMultiButton;
}

export class ModalSelectElement {
    constructor(public elementText: string, public value: any) { }
}

export class ModalMultiButton extends ModalSelectElement {
    constructor(elementText: string, value: any, public cssClass: string = 'btn-primary') {
        super(elementText, value);
    }
}

export class ModalSettingsSelectMultiChoice extends ModalSettingsConfirm {

    constructor(title?: string, body?: string, type?: SelectMultiChoiceTypes, okText?: string, cancelText?: string) {
        super(title, body, okText, cancelText);
        this.multiChoiceType = type || SelectMultiChoiceTypes.DropDown;
    }

    multiChoiceType: SelectMultiChoiceTypes;
    selectedElement: ModalSelectElement;
    elements: ModalSelectElement[] = [];
    override modalType: ModalTypes = ModalTypes.SelectMultiChoice;
}

export enum SelectMultiChoiceTypes {
    DropDown,
    RadioButtons,
    CheckBoxes
}

export class ModalSettingsFileSelector extends ModalSettingsConfirm {
    constructor(
        title?: string,
        body?: string,
        public allowMultiple: boolean = false,
        okText?: string,
        cancelText?: string,
        public fileFilter: string = '*.*',
        public maxFiles?: number,
        public maxFileSize?: number
    ) {
        super(title, body, okText, cancelText);
    }

    override modalType: ModalTypes = ModalTypes.FileSelector;
}

export class ModalSettingsDateTime extends ModalSettingsConfirm {
    constructor(title?: string, body?: string, okText?: string, cancelText?: string) {
        super(title, body, okText, cancelText);
    }

    type = 'date';
    minDate: Date;
    timeInterval = 30;

    override modalType: ModalTypes = ModalTypes.DateTime;
}
